import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gavel, Save, ShieldAlert, Undo2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { BTN_PRIMARY } from "@/pages/Servers";
import { RuleWatch } from "@/components/ranger/RuleWatch";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Card, EmptyState } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { ConfirmButton } from "@/components/ranger/ConfirmButton";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const Row = ({ w, canRevoke, onRevoke }) => (
  <TableRow data-testid={`warning-row-${w.id}`} className={`border-[#1E2D4A]/60 text-xs ${w.revoked ? "opacity-50" : ""}`}>
    <TableCell className="whitespace-nowrap text-slate-400">{fmtTime(w.created_at)}</TableCell>
    <TableCell className="font-display text-base font-semibold text-slate-100">{w.gamertag}</TableCell>
    <TableCell><StatusBadge state={w.severity === "major" ? "error" : "unconfigured"} label={w.severity} /></TableCell>
    <TableCell className="max-w-md text-slate-300">{w.reason}</TableCell>
    <TableCell className="text-slate-400">{w.issued_by}</TableCell>
    <TableCell>{w.revoked ? <span className="text-slate-500">revoked by {w.revoked_by}</span> : canRevoke && (
      <ConfirmButton testId={`warning-revoke-${w.id}`} className="text-slate-300 hover:text-red-400" title="Revoke warning?" description={`Remove this warning from ${w.gamertag}'s active count. It stays in history.`} onConfirm={() => onRevoke(w.id)}><Undo2 className="h-4 w-4" /></ConfirmButton>
    )}</TableCell>
  </TableRow>
);

const Escalation = ({ cid, canEdit }) => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get(`/communities/${cid}/moderation-config`).then((r) => setCfg(r.data)); }, [cid]);
  if (!cfg) return null;
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put(`/communities/${cid}/moderation-config`, { escalation_enabled: cfg.escalation_enabled, kick_threshold: Number(cfg.kick_threshold) }); setCfg(data); toast.success("Escalation saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card title="Warning escalation" icon={ShieldAlert} testId="escalation-card" className="mb-5">
      <form onSubmit={save} className="flex flex-wrap items-end gap-5">
        <label className="flex items-center gap-3 text-sm text-slate-300"><Switch data-testid="escalation-switch" disabled={!canEdit} checked={cfg.escalation_enabled} onCheckedChange={(v) => setCfg({ ...cfg, escalation_enabled: v })} />Auto-kick</label>
        <label className="space-y-1.5"><span className="rg-label block">At active warnings</span><Input data-testid="escalation-threshold-input" type="number" min={1} max={20} disabled={!canEdit} value={cfg.kick_threshold} onChange={(e) => setCfg({ ...cfg, kick_threshold: e.target.value })} className="w-28 border-[#1E2D4A] bg-[#050811] font-mono" /></label>
        {canEdit && <button data-testid="escalation-save-button" className={BTN_PRIMARY}><Save className="h-4 w-4" />Save</button>}
        <p className="basis-full text-xs text-slate-500">When a warning brings a player to this many active warnings, RANGER kicks them if they are online and alerts staff in Discord. Offline players are not kicked; staff are alerted instead.</p>
      </form>
    </Card>
  );
};

export default function Moderation() {
  const { active, can } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setTerm(q), 350);
    return () => clearTimeout(t);
  }, [q]);
  const load = useCallback(() => api.get(`/communities/${active.id}/warnings${term ? `?gamertag=${encodeURIComponent(term)}` : ""}`).then((r) => setRows(r.data)).catch(() => setRows([])), [active.id, term]);
  useEffect(() => { load(); }, [load]);
  const revoke = async (id) => {
    try { await api.delete(`/communities/${active.id}/warnings/${id}`); toast.success("Warning revoked"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!can("moderator")) return <EmptyState title="Restricted">Moderator role required.</EmptyState>;
  return (
    <div>
      <PageHeader eyebrow="Phase 2 · Moderation" title="Moderation" description="Warning history for this community. Issue warnings from the Online now list on the Players page." />
      <Escalation cid={active.id} canEdit={can("admin")} />
      {active.mode === "pve_purge" && <RuleWatch cid={active.id} canEdit={can("admin")} />}
      <Card title="Warnings" icon={Gavel} right={<Input data-testid="warnings-search-input" placeholder="Gamertag starts with…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-56 border-[#1E2D4A] bg-[#050811] font-mono text-xs" />}>
        {rows.length === 0 ? <EmptyState title="No warnings" testId="warnings-empty">Warnings logged by staff appear here.</EmptyState> : (
          <div className="overflow-x-auto">
            <Table data-testid="warnings-table">
              <TableHeader><TableRow className="border-[#1E2D4A]">{["Time", "Player", "Severity", "Reason", "Issued by", ""].map((h) => <TableHead key={h} className="rg-label whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>{rows.map((w) => <Row key={w.id} w={w} canRevoke={can("admin")} onRevoke={revoke} />)}</TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
